import { useState, useRef } from "react";
import { Camera } from "lucide-react";
import api, { formatApiError, imgUrl } from "../lib/api";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";

const RETURN_REASONS = ["Item arrived damaged", "Item is defective", "Wrong item delivered", "Item not as described", "Other"];
const MOD_TYPES = [["address", "Correct delivery address"], ["quantity", "Change quantity"], ["product", "Change a product"], ["other", "Something else"]];

export function ReturnDialog({ order, email, onClose, onDone }) {
  const [type, setType] = useState("Return");
  const [reason, setReason] = useState("");
  const [description, setDescription] = useState("");
  const [image, setImage] = useState("");
  const [selected, setSelected] = useState(order.items.map((i) => i.product_id));
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/customer-image", fd);
      setImage(data.url);
      toast.success("Photo attached");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!reason) return toast.error("Please select a reason");
    if (!selected.length) return toast.error("Please select at least one item");
    setLoading(true);
    try {
      await api.post(`/orders/${order.order_number}/return-request`, {
        email, type, reason, description, image,
        items: order.items.filter((i) => selected.includes(i.product_id)).map((i) => ({ product_id: i.product_id, qty: i.qty })),
      });
      toast.success("Request submitted — our team will review it");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#FAF9F6] max-w-md max-h-[90vh] overflow-y-auto" data-testid="return-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Return / Refund / Exchange — {order.order_number}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground leading-relaxed">As per our Return & Refund Policy, we accept returns for damaged or defective items reported within 48 hours of delivery. Refunds are processed manually to the original payment method after our team approves the request.</p>
        <form onSubmit={submit} className="flex flex-col gap-4 mt-2">
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="rounded-sm bg-white" data-testid="return-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Return" data-testid="return-type-return">Return</SelectItem>
              <SelectItem value="Refund" data-testid="return-type-refund">Refund</SelectItem>
              <SelectItem value="Exchange" data-testid="return-type-exchange">Exchange</SelectItem>
            </SelectContent>
          </Select>
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Items</p>
            {order.items.map((i) => (
              <label key={i.product_id} className="flex items-center gap-2 text-sm py-1" data-testid={`return-item-${i.product_id}`}>
                <input type="checkbox" checked={selected.includes(i.product_id)}
                  onChange={(e) => setSelected(e.target.checked ? [...selected, i.product_id] : selected.filter((x) => x !== i.product_id))} />
                {i.name} × {i.qty}
              </label>
            ))}
          </div>
          <Select value={reason} onValueChange={setReason}>
            <SelectTrigger className="rounded-sm bg-white" data-testid="return-reason"><SelectValue placeholder="Reason *" /></SelectTrigger>
            <SelectContent>
              {RETURN_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
          <Textarea placeholder="Describe the issue (optional)" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} className="bg-white" data-testid="return-description" />
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
              className="border border-black/20 px-4 py-2 text-xs uppercase tracking-widest hover:border-gold hover:text-gold transition-colors duration-300 flex items-center gap-2"
              data-testid="return-photo-upload">
              <Camera strokeWidth={1.5} size={14} /> {uploading ? "Uploading..." : "Add Photo"}
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} />
            {image && <img src={imgUrl(image)} alt="Evidence" className="w-12 h-12 object-cover border border-black/10" data-testid="return-photo-preview" />}
          </div>
          <div className="flex gap-3">
            <button type="submit" disabled={loading} className="flex-1 bg-[#111] text-[#FAF9F6] py-3 small-caps hover:bg-gold hover:text-[#111] transition-colors duration-300 disabled:opacity-50" data-testid="return-submit">
              {loading ? "Submitting..." : "Submit Request"}
            </button>
            <button type="button" onClick={onClose} className="flex-1 border border-black/20 py-3 small-caps hover:border-gold transition-colors duration-300" data-testid="return-cancel">Cancel</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ModDialog({ order, email, onClose, onDone }) {
  const [type, setType] = useState("address");
  const [message, setMessage] = useState("");
  const [addr, setAddr] = useState(order.address || { line1: "", line2: "", city: "", state: "", pincode: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim() && type !== "address") return toast.error("Please describe the change");
    setLoading(true);
    try {
      await api.post(`/orders/${order.order_number}/modification-request`, {
        email, type, message: message.trim() || "Address correction requested",
        address: type === "address" ? addr : undefined,
      });
      toast.success("Change request sent — our team will confirm before applying it");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#FAF9F6] max-w-md max-h-[90vh] overflow-y-auto" data-testid="mod-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Request Order Change — {order.order_number}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground leading-relaxed">Your order is never changed silently. Our team reviews every request and confirms with you before applying any change.</p>
        <form onSubmit={submit} className="flex flex-col gap-4 mt-2">
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="rounded-sm bg-white" data-testid="mod-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              {MOD_TYPES.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`mod-type-${v}`}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          {type === "address" && (
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Address Line 1 *" value={addr.line1} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} className="col-span-2 bg-white" data-testid="mod-addr-line1" />
              <Input placeholder="Address Line 2" value={addr.line2 || ""} onChange={(e) => setAddr({ ...addr, line2: e.target.value })} className="col-span-2 bg-white" data-testid="mod-addr-line2" />
              <Input placeholder="City *" value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} className="bg-white" data-testid="mod-addr-city" />
              <Input placeholder="State *" value={addr.state} onChange={(e) => setAddr({ ...addr, state: e.target.value })} className="bg-white" data-testid="mod-addr-state" />
              <Input placeholder="Pincode *" value={addr.pincode} onChange={(e) => setAddr({ ...addr, pincode: e.target.value })} className="bg-white" data-testid="mod-addr-pincode" />
            </div>
          )}
          <Textarea placeholder={type === "address" ? "Note for our team (optional)" : "Describe the change you need *"} rows={3} value={message} onChange={(e) => setMessage(e.target.value)} className="bg-white" data-testid="mod-message" />
          <div className="flex gap-3">
            <button type="submit" disabled={loading} className="flex-1 bg-[#111] text-[#FAF9F6] py-3 small-caps hover:bg-gold hover:text-[#111] transition-colors duration-300 disabled:opacity-50" data-testid="mod-submit">
              {loading ? "Sending..." : "Send Request"}
            </button>
            <button type="button" onClick={onClose} className="flex-1 border border-black/20 py-3 small-caps hover:border-gold transition-colors duration-300" data-testid="mod-cancel">Cancel</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
